import { useEffect, useMemo, useRef, useState } from 'react';

import { Select, Spin } from 'antd';
import debounce from 'lodash.debounce';
import useCloudFunction from '@src/packages/utils/hooks/clouds/useCloudFunction';
import IntlMessages from '@src/util/IntlMessages';

import styles from './styles.module.less';

/**
 * A debounced select component that fetches data from a cloud function.
 * It supports searching, pagination, and loading more data on scroll.
 *
 * @component
 * @param {Object} props - The component props
 * @param {'legacy' | 'elastic'} [props.queryMode='legacy'] - API payload format
 * @param {string} props.op - API operation to call
 * @param {Object} [props.params={}] - Additional parameters to pass to the cloud function
 * @param {string} [props.placeholder='Buscar...'] - Placeholder text for the select input
 * @param {any} props.value - Current value of the select
 * @param {string} [props.selectedLabel=''] - Label for the current value when it is not in options
 * @param {Function} props.onChange - Callback function when selection changes
 * @param {string} [props.fieldToSearch='search'] - Legacy mode: field name for search in request params
 * @param {string|string[]} [props.searchKey='firstName'] - Elasticsearch field(s) for prefix search (e.g. firstName:Rica*)
 * @param {Function} [props.parseData=null] - Function to transform the data received from the cloud function
 * @returns {JSX.Element} A Select component with debounced search and infinite scroll
 */

const PER_PAGE = 20;
const EMPTY_PARAMS = {};
const LOADING_OPTION_VALUE = '__debounce_cloud_fetch_loading__';

const SelectDropdownLoading = () => (
  <div className={styles.dropdownLoading} aria-busy="true" aria-live="polite">
    <Spin size="small" />
    <span className={styles.dropdownLoadingText}>
      <IntlMessages id="general.loading.options" />
    </span>
  </div>
);

const areOptionsEqual = (currentOptions, nextOptions) => {
  if (currentOptions.length !== nextOptions.length) return false;

  return currentOptions.every(
    (option, index) =>
      String(option.value) === String(nextOptions[index]?.value) &&
      String(option.label) === String(nextOptions[index]?.label),
  );
};

const DebounceCloudFetchSelect = ({
  disabled = false,
  queryMode = 'legacy',
  op,
  params = EMPTY_PARAMS,
  placeholder = 'Buscar...',
  emptyPlaceholder = 'general.empty.state',
  value,
  selectedLabel = '',
  onChange,
  fieldToSearch = 'search',
  searchKey = 'firstName',
  parseData = null,
}) => {
  const [options, setOptions] = useState([]);
  const [_, setHasMore] = useState(true);
  const [resetPage, setResetPage] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const paginationRef = useRef(0);
  const hasMoreRef = useRef(true);
  const valueRef = useRef(value);
  const selectedLabelRef = useRef(selectedLabel);
  const fetchDataRef = useRef(null);
  const parseDataRef = useRef(parseData);
  const paramsRef = useRef(params);
  const queryModeRef = useRef(queryMode);
  const fieldToSearchRef = useRef(fieldToSearch);
  const searchKeyRef = useRef(searchKey);
  const canFetchRef = useRef(false);
  const isFetchingRef = useRef(false);
  const isResolvingMissingOptionRef = useRef(false);
  const lastSearchTermRef = useRef('');
  const resolvedValuesRef = useRef(new Set());

  const pageSize = params.pagination?.size ?? PER_PAGE;
  const pageSizeRef = useRef(pageSize);
  pageSizeRef.current = pageSize;

  const { loading, mutateAsync: fetchData } = useCloudFunction({
    runFirstTime: false,
    op,
  });

  fetchDataRef.current = fetchData;
  parseDataRef.current = parseData;
  paramsRef.current = params;
  queryModeRef.current = queryMode;
  fieldToSearchRef.current = fieldToSearch;
  searchKeyRef.current = searchKey;

  const buildSearchQuery = search => {
    const currentSearchKey = searchKeyRef.current;

    if (Array.isArray(currentSearchKey)) {
      return currentSearchKey
        .map(key => {
          if (key === 'idCard') return `${key}:${search.replace(/\./g, '')}*`;
          return `${key}:${search}*`;
        })
        .join(' OR ');
    }

    return `${currentSearchKey}:${search}*`;
  };

  const mergeSelectedOption = optionsList => {
    const currentValue = valueRef.current;
    const currentLabel = selectedLabelRef.current;

    if (!currentValue || !currentLabel) return optionsList;

    const hasSelectedOption = optionsList.some(
      option => String(option.value) === String(currentValue),
    );

    if (hasSelectedOption) return optionsList;

    return [{ label: currentLabel, value: currentValue }, ...optionsList];
  };

  const buildRequestParams = (from, searchText) => {
    const trimmedSearch = searchText?.trim() ?? '';
    const currentParams = paramsRef.current;
    const currentPageSize = pageSizeRef.current;

    if (queryModeRef.current === 'elastic') {
      const requestParams = {
        ...currentParams,
        pagination: {
          ...(currentParams.pagination || {}),
          from,
          size: currentPageSize,
        },
      };

      if (trimmedSearch) {
        requestParams.filters = {
          ...(currentParams.filters || {}),
          textSearch: buildSearchQuery(trimmedSearch),
        };
      }

      return requestParams;
    }

    const requestParams = {
      ...currentParams,
      lastPosition: from,
    };

    if (trimmedSearch) {
      requestParams[fieldToSearchRef.current] = buildSearchQuery(trimmedSearch);
    }

    return requestParams;
  };

  const canFetch = useMemo(() => {
    if (queryMode === 'elastic') {
      return Boolean(params.organizationId);
    }

    const allParamsHaveValues = Object.values(params).every(
      paramValue => paramValue !== undefined && paramValue !== null && paramValue !== '',
    );

    return allParamsHaveValues || Object.keys(params).length === 0;
  }, [params, queryMode]);

  canFetchRef.current = canFetch;

  const paramsKey = useMemo(
    () =>
      JSON.stringify({
        queryMode,
        op,
        params,
        fieldToSearch,
        searchKey,
        pageSize,
      }),
    [fieldToSearch, op, pageSize, params, queryMode, searchKey],
  );

  const fetchOptions = async (searchText = '', append = false) => {
    if (!canFetchRef.current || !fetchDataRef.current || isFetchingRef.current) return;

    const normalizedSearch = searchText?.trim() ?? '';

    if (!append && normalizedSearch === lastSearchTermRef.current && paginationRef.current > 0) {
      return;
    }

    const from = append ? paginationRef.current : 0;

    if (append && !hasMoreRef.current) return;

    isFetchingRef.current = true;
    setFetching(true);
    setLoadingMore(append);

    try {
      const response = await fetchDataRef.current(buildRequestParams(from, searchText));

      if (!response?.success) return;

      const rawData = response.data;
      const currentParseData = parseDataRef.current;
      const parsedOptions = currentParseData ? currentParseData(rawData) : rawData;
      const nextOptions = Array.isArray(parsedOptions) ? parsedOptions : [];
      const totalHits = rawData?.hits?.total?.value ?? 0;

      if (append) {
        setOptions(prevOptions => {
          const mergedOptions = mergeSelectedOption([...prevOptions, ...nextOptions]);
          const nextHasMore = totalHits > mergedOptions.length;
          hasMoreRef.current = nextHasMore;
          setHasMore(nextHasMore);
          return mergedOptions;
        });
      } else {
        const mergedOptions = mergeSelectedOption(nextOptions);
        const nextHasMore = totalHits > mergedOptions.length;
        setOptions(mergedOptions);
        hasMoreRef.current = nextHasMore;
        setHasMore(nextHasMore);
        lastSearchTermRef.current = normalizedSearch;
      }

      paginationRef.current = from + pageSizeRef.current;
    } catch (error) {
      // noop
    } finally {
      isFetchingRef.current = false;
      setFetching(false);
      setLoadingMore(false);
    }
  };

  const fetchOptionsRef = useRef(fetchOptions);
  fetchOptionsRef.current = fetchOptions;

  const loadMoreRef = useRef(
    debounce((searchText, append) => {
      fetchOptionsRef.current(searchText, append);
    }, 500),
  );

  useEffect(() => {
    valueRef.current = value;
    selectedLabelRef.current = selectedLabel;
  }, [selectedLabel, value]);

  useEffect(() => {
    return () => {
      loadMoreRef.current.cancel();
    };
  }, []);

  useEffect(() => {
    if (!canFetch) {
      setOptions([]);
      setLoadingMore(false);
      paginationRef.current = 0;
      hasMoreRef.current = false;
      setHasMore(false);
      return;
    }

    paginationRef.current = 0;
    hasMoreRef.current = true;
    lastSearchTermRef.current = '';
    setHasMore(true);
    fetchOptionsRef.current('', false);
  }, [canFetch, paramsKey, resetPage]);

  useEffect(() => {
    if (!value) return;

    setOptions(prevOptions => {
      const mergedOptions = mergeSelectedOption(prevOptions);
      return areOptionsEqual(prevOptions, mergedOptions) ? prevOptions : mergedOptions;
    });
  }, [selectedLabel, value]);

  useEffect(() => {
    resolvedValuesRef.current = new Set();
  }, [paramsKey, resetPage]);

  useEffect(() => {
    if (queryMode !== 'elastic') return;
    if (!value || selectedLabel) return;
    if (!canFetch || !params.organizationId) return;
    if (isFetchingRef.current || isResolvingMissingOptionRef.current) return;

    const valueKey = String(value);
    if (resolvedValuesRef.current.has(valueKey)) return;

    const hasOption = options.some(option => String(option.value) === valueKey);
    if (hasOption) return;

    const resolveMissingOption = async () => {
      if (!fetchDataRef.current) return;

      isResolvingMissingOptionRef.current = true;
      resolvedValuesRef.current.add(valueKey);

      try {
        const currentParams = paramsRef.current;
        const response = await fetchDataRef.current({
          ...currentParams,
          filters: { terms: { id: [value] } },
          pagination: { from: 0, size: 1 },
        });

        if (!response?.success) return;

        const currentParseData = parseDataRef.current;
        const parsedOptions = currentParseData ? currentParseData(response.data) : response.data;
        const resolvedOption = Array.isArray(parsedOptions) ? parsedOptions[0] : null;

        if (!resolvedOption) return;

        setOptions(prevOptions => {
          if (prevOptions.some(option => String(option.value) === String(resolvedOption.value))) {
            return prevOptions;
          }

          return [resolvedOption, ...prevOptions];
        });
      } catch (error) {
        // noop
      } finally {
        isResolvingMissingOptionRef.current = false;
      }
    };

    resolveMissingOption();
  }, [canFetch, options, params.organizationId, queryMode, selectedLabel, value]);

  const handleSearch = searchText => {
    loadMoreRef.current(searchText, false);
  };

  const isLoading = loading || fetching;

  const displayOptions = useMemo(() => {
    if (!loadingMore) return options;

    return [
      ...options,
      {
        label: (
          <div className={styles.loadingMore}>
            <Spin size="small" />
          </div>
        ),
        value: LOADING_OPTION_VALUE,
        disabled: true,
      },
    ];
  }, [loadingMore, options]);

  const handlePopupScroll = event => {
    const { target } = event;

    if (isLoading) return;

    if (target.scrollTop + target.offsetHeight < target.scrollHeight - 20) return;

    const selectElement = target.closest('.ant-select');
    const currentSearchValue = selectElement?.querySelector('input')?.value ?? '';
    loadMoreRef.current(currentSearchValue, true);
  };

  return (
    <Select
      getPopupContainer={trigger => trigger.parentNode}
      disabled={disabled}
      showSearch
      allowClear
      onClear={() => setResetPage(prev => !prev)}
      placeholder={placeholder}
      filterOption={false}
      options={displayOptions}
      loading={isLoading}
      className="gx-w-100"
      onSearch={handleSearch}
      notFoundContent={
        isLoading ? <SelectDropdownLoading /> : <IntlMessages id={emptyPlaceholder} />
      }
      value={value ?? undefined}
      listHeight={200}
      onPopupScroll={handlePopupScroll}
      onChange={onChange}
    />
  );
};

export default DebounceCloudFetchSelect;
